import L from 'leaflet';

export interface ILatLngLike {
  lat: number;
  lng: number;
}

interface IPoint {
  x: number;
  y: number;
}

export interface IProjector {
  project: (point: ILatLngLike) => IPoint;
  unproject: (point: IPoint) => ILatLngLike;
}

const add = (a: IPoint, b: IPoint): IPoint => ({ x: a.x + b.x, y: a.y + b.y });

const sub = (a: IPoint, b: IPoint): IPoint => ({ x: a.x - b.x, y: a.y - b.y });

const scale = (p: IPoint, k: number): IPoint => ({ x: p.x * k, y: p.y * k });

const dot = (a: IPoint, b: IPoint): number => a.x * b.x + a.y * b.y;

const length = (p: IPoint): number => Math.hypot(p.x, p.y);

const normalize = (p: IPoint): IPoint => {
  const len = length(p);
  if (len === 0) {
    return { x: 1, y: 0 };
  }
  return { x: p.x / len, y: p.y / len };
};

const averagePoint = (points: IPoint[]): IPoint => {
  const sum = points.reduce((acc, point) => add(acc, point), { x: 0, y: 0 });
  return { x: sum.x / points.length, y: sum.y / points.length };
};

export const createLeafletMapProjector = (map: L.Map): IProjector => ({
  project: ({ lat, lng }) => {
    const layerPoint = map.latLngToLayerPoint(L.latLng(lat, lng));
    return { x: layerPoint.x, y: layerPoint.y };
  },
  unproject: ({ x, y }) => {
    const latLng = map.layerPointToLatLng(L.point(x, y));
    return { lat: latLng.lat, lng: latLng.lng };
  }
});

/** Approximate meter projection for unit tests without a Leaflet map instance. */
export const createEquirectangularProjector = (referenceLat: number): IProjector => {
  const lngScale = 111320 * Math.cos((referenceLat * Math.PI) / 180);
  const latScale = 111320;

  return {
    project: ({ lat, lng }) => ({ x: lng * lngScale, y: lat * latScale }),
    unproject: ({ x, y }) => ({ lng: x / lngScale, lat: y / latScale })
  };
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const expandBboxToGbox = (bbox: unknown): number[] | null => {
  const normalized = normalizeGboxLength(bbox, 4);

  if (!normalized) {
    return null;
  }

  const [x0, y0, x1, y1] = normalized;

  return [x0, y0, x1, y0, x0, y1, x1, y1];
};

const normalizeGboxLength = (value: unknown, length: number): number[] | null => {
  if (typeof value === 'string') {
    const parts = value.split(',').map(part => part.trim());

    if (parts.length === length) {
      return normalizeGboxLength(parts, length);
    }
  }

  if (!Array.isArray(value) || value.length !== length) {
    return null;
  }

  const normalized = value.map(toFiniteNumber);

  if (normalized.some(item => item === null)) {
    return null;
  }

  return normalized as number[];
};

const isZeroGbox = (normalized: number[]): boolean => normalized.every(value => value === 0);

/**
 * Normalizes any supported gbox shape into the flat 8-point array, or null:
 *  - nested corner-pair arrays (e.g. [[x,y], ...]) are flattened;
 *  - an 8-point gbox, tolerating extra trailing elements — the backend appends one (observed
 *    length 9), so arrays/strings longer than 8 use the first 8, matching the legacy index 0..7
 *    read (this is documented, not silent: see readGboxCorners);
 *  - a 4-point geo bbox, expanded via expandBboxToGbox.
 * Single source of truth shared by readGboxCorners / isValidGbox / getGboxKey so every consumer
 * agrees on whether a gbox is present (HeatMap, Tracks and the main ImageOverlay).
 */
export const normalizeGbox = (gbox: unknown): number[] | null => {
  if (Array.isArray(gbox) && gbox.length > 0 && gbox.every(item => Array.isArray(item))) {
    return normalizeGbox(gbox.flat());
  }

  const items =
    typeof gbox === 'string'
      ? gbox.split(',').map(part => part.trim())
      : Array.isArray(gbox)
        ? gbox
        : null;

  if (items && items.length >= 8) {
    return normalizeGboxLength(items.slice(0, 8), 8);
  }

  if (items && items.length === 4) {
    return expandBboxToGbox(items);
  }

  return null;
};

/** Reads the 4 overlay corners from a gbox of any supported shape, or null when missing/invalid. */
export const readGboxCorners = (gbox: unknown): ILatLngLike[] | null => {
  const normalized = normalizeGbox(gbox);

  if (!normalized || isZeroGbox(normalized)) {
    return null;
  }

  return gboxToCorners(normalized);
};

export const isValidGbox = (gbox: unknown): boolean => {
  const normalized = normalizeGbox(gbox);

  return normalized !== null && !isZeroGbox(normalized);
};

export const getGboxKey = (gbox: unknown): string => {
  const normalized = normalizeGbox(gbox);

  if (!normalized || isZeroGbox(normalized)) {
    return '';
  }

  return normalized.join(',');
};

export const getOverlayCornersFromGbox = (gbox: unknown): ILatLngLike[] | null =>
  readGboxCorners(gbox);

/**
 * gbox layout: [x2,y2, x3,y3, x0,y0, x1,y1] matching DistortableImageOverlay corner order.
 */
export const gboxToCorners = (gbox: number[]): ILatLngLike[] => [
  { lat: gbox[5], lng: gbox[4] },
  { lat: gbox[7], lng: gbox[6] },
  { lat: gbox[1], lng: gbox[0] },
  { lat: gbox[3], lng: gbox[2] }
];

export const cornersToGbox = (corners: ILatLngLike[]): number[] => [
  corners[2].lng,
  corners[2].lat,
  corners[3].lng,
  corners[3].lat,
  corners[0].lng,
  corners[0].lat,
  corners[1].lng,
  corners[1].lat
];

const getRectangleAxes = (points: IPoint[]) => {
  const widthDirection = normalize(add(sub(points[1], points[0]), sub(points[3], points[2])));
  const heightEdge = add(sub(points[2], points[0]), sub(points[3], points[1]));
  let heightDirection: IPoint = { x: -widthDirection.y, y: widthDirection.x };

  if (dot(heightEdge, heightDirection) < 0) {
    heightDirection = scale(heightDirection, -1);
  }

  return { widthDirection, heightDirection };
};

const getHalfExtents = (
  points: IPoint[],
  center: IPoint,
  widthDirection: IPoint,
  heightDirection: IPoint
) => {
  let halfWidth = 0;
  let halfHeight = 0;

  for (const point of points) {
    const delta = sub(point, center);
    halfWidth = Math.max(halfWidth, Math.abs(dot(delta, widthDirection)));
    halfHeight = Math.max(halfHeight, Math.abs(dot(delta, heightDirection)));
  }

  return { halfWidth, halfHeight };
};

interface IRectangleGeometry {
  center: IPoint;
  widthDirection: IPoint;
  heightDirection: IPoint;
  halfWidth: number;
  halfHeight: number;
}

/**
 * Projects the corners and derives the rectangle's centroid, oriented axes, and half extents.
 * Shared by aspect-ratio computation and rectangular constraining.
 */
const getRectangleGeometry = (
  corners: ILatLngLike[],
  projector: IProjector
): IRectangleGeometry | null => {
  if (corners.length !== 4) {
    return null;
  }

  const points = corners.map(projector.project);
  const center = averagePoint(points);
  const { widthDirection, heightDirection } = getRectangleAxes(points);
  const { halfWidth, halfHeight } = getHalfExtents(points, center, widthDirection, heightDirection);

  return { center, widthDirection, heightDirection, halfWidth, halfHeight };
};

export const getAspectRatioFromCorners = (
  corners: ILatLngLike[],
  projector: IProjector
): number => {
  const geometry = getRectangleGeometry(corners, projector);

  if (!geometry) {
    return 1;
  }

  const { halfWidth, halfHeight } = geometry;

  if (halfHeight === 0) {
    // Degenerate (collinear) source: signal an invalid ratio so the caller skips
    // constraining instead of snapping to an arbitrary square.
    return Infinity;
  }

  return halfWidth / halfHeight;
};

const cornersAreEqual = (a: ILatLngLike[], b: ILatLngLike[], tolerance = 1e-9): boolean =>
  a.every((corner, index) => {
    const other = b[index];
    return (
      Math.abs(corner.lat - other.lat) <= tolerance &&
      Math.abs(corner.lng - other.lng) <= tolerance
    );
  });

// Beyond this width/height ratio a plan is treated as degenerate (e.g. a nearly-collinear
// source gbox produced a pathological aspect ratio). Constraining with such a ratio would
// collapse the rectangle to a sliver, so the corners are left untouched instead.
const MAX_ASPECT_RATIO = 100;

// Corners are always ordered [TL, TR, BL, BR] (see gboxToCorners), so each corner maps to a
// fixed quadrant of the rectangle. Deriving the quadrant from each corner's offset to the
// centroid collapses two corners to the same point when a single corner is dragged far
// enough to pull the centroid outside the quad.
const RECTANGLE_CORNER_SIGNS: ReadonlyArray<{ u: number; v: number }> = [
  { u: -1, v: -1 },
  { u: 1, v: -1 },
  { u: -1, v: 1 },
  { u: 1, v: 1 }
];

/**
 * Rebuilds corners as a rectangle (90° angles) in projected map space, preserving the
 * centroid, orientation, and the configured width/height aspect ratio. Each corner keeps a
 * fixed quadrant (see RECTANGLE_CORNER_SIGNS) so the result never collapses.
 */
export const constrainRectangularCorners = (
  corners: ILatLngLike[],
  aspectRatio: number,
  projector: IProjector
): ILatLngLike[] => {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return corners;
  }

  const geometry = getRectangleGeometry(corners, projector);

  if (!geometry) {
    return corners;
  }

  let { halfWidth, halfHeight } = geometry;
  const { center, widthDirection, heightDirection } = geometry;

  if (halfWidth === 0 || halfHeight === 0) {
    return corners;
  }

  if (aspectRatio > MAX_ASPECT_RATIO || aspectRatio < 1 / MAX_ASPECT_RATIO) {
    return corners;
  }

  if (halfWidth / halfHeight > aspectRatio) {
    halfWidth = halfHeight * aspectRatio;
  } else {
    halfHeight = halfWidth / aspectRatio;
  }

  return RECTANGLE_CORNER_SIGNS.map(({ u, v }) => {
    const projected = add(
      center,
      add(scale(widthDirection, u * halfWidth), scale(heightDirection, v * halfHeight))
    );

    return projector.unproject(projected);
  });
};

export const constrainRectangularCornersIfNeeded = (
  corners: ILatLngLike[],
  aspectRatio: number,
  projector: IProjector
): { corners: ILatLngLike[]; changed: boolean } => {
  const constrained = constrainRectangularCorners(corners, aspectRatio, projector);
  return {
    corners: constrained,
    changed: !cornersAreEqual(corners, constrained)
  };
};
